import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { HandCoins, Gem, Palette, Laptop, Wrench, Bike, Store, ChevronLeft, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Goldman & Sons Pawn — the city's second-hand counter. Instant-pawn valuables for
   40% now, or consign at your own ask: the shelf works while you're offline, and the
   payout lands in your bank with a letter. Stock is per-server persistent. */

const CAT_ICON = { jewellery: Gem, art: Palette, electronics: Laptop, tools: Wrench, garage: Bike };
const CAT_COLOR = { jewellery: "#e8b93e", art: "#a78bfa", electronics: "#38bdf8", tools: "#f59e0b", garage: "#4ade80" };

const iso = (minAgo) => new Date(Date.now() - minAgo * 60000).toISOString();
const DEMO_STOCK = [
  { id: "ps1", key: "designer_watch", label: "Designer Watch", cat: "jewellery", qty: 1, price_unit: 1190, total: 1190, seller_name: "Goldman & Sons Pawn", shop_stock: true, mine: false, listed_at: iso(300) },
  { id: "ps2", key: "oil_painting", label: "Oil Painting", cat: "art", qty: 1, price_unit: 3400, total: 3400, seller_name: "Ada King", shop_stock: false, mine: false, listed_at: iso(140) },
  { id: "ps3", key: "game_console", label: "Game Console", cat: "electronics", qty: 2, price_unit: 300, total: 600, seller_name: "Goldman & Sons Pawn", shop_stock: true, mine: false, listed_at: iso(90) },
];
const DEMO_MINE = {
  valuables: [
    { key: "gold_chain", label: "Gold Chain", cat: "jewellery", value: 850, qty: 2 },
    { key: "flatscreen", label: "Flatscreen TV", cat: "electronics", value: 540, qty: 1 },
    { key: "power_tools", label: "Power Tools", cat: "tools", value: 260, qty: 3 },
  ],
  listings: [
    { id: "pm1", key: "designer_watch", label: "Designer Watch", cat: "jewellery", qty: 1, price_unit: 1500, total: 1500, seller_name: "You", status: "sold", payout: 1350, buyer_name: "Ray Dalton", listed_at: iso(2000) },
    { id: "pm2", key: "antique_vase", label: "Antique Vase", cat: "art", qty: 1, price_unit: 2400, total: 2400, seller_name: "You", status: "listed", payout: 0, buyer_name: "", listed_at: iso(500) },
  ],
};

function ItemIcon({ cat, className = "w-4 h-4" }) {
  const Icon = CAT_ICON[cat] || Gem;
  return <Icon className={className} style={{ color: CAT_COLOR[cat] || "#e8b93e" }} />;
}

export default function PawnApp({ demo, serverId, token, accent, sfx }) {
  const [tab, setTab] = useState("browse"); // browse | sell | mine
  const [stock, setStock] = useState(demo ? DEMO_STOCK : []);
  const [mine, setMine] = useState(demo ? DEMO_MINE : { valuables: [], listings: [] });
  const [rates, setRates] = useState({ instant: 0.4, house_cut: 0.1, consign_hours: 72 });
  const [sell, setSell] = useState(null); // valuable row being sold
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState("instant");
  const [ask, setAsk] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/pawn/stock`, { params: { token } })
      .then((r) => { setStock(r.data.stock); setRates(r.data.rates); }).catch(() => {});
    api.get(`/nc/servers/${serverId}/pawn/my`, { params: { token } })
      .then((r) => setMine(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const startSell = (v) => { sfx?.keyTick(); setSell(v); setQty(1); setMode("instant"); setAsk(v.value); };

  const confirmSell = async () => {
    if (!sell || busy) return;
    setBusy(true);
    if (demo) {
      toast.success(mode === "instant"
        ? `Sold ${qty}x ${sell.label} for $${(Math.floor(sell.value * rates.instant) * qty).toLocaleString()} (demo)`
        : `Listed ${qty}x ${sell.label} at $${ask.toLocaleString()} each (demo)`);
      setBusy(false); setSell(null); return;
    }
    try {
      const r = await api.post(`/nc/servers/${serverId}/pawn/sell`,
        { key: sell.key, qty, mode, ask: mode === "consign" ? ask : 0 }, { params: { token } });
      toast.success(r.data.message);
      sfx?.success();
      setSell(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const buy = async (l) => {
    if (busy) return;
    setBusy(true);
    if (demo) { toast.success(`Bought ${l.qty}x ${l.label} for $${l.total.toLocaleString()} (demo)`); setBusy(false); return; }
    try {
      const r = await api.post(`/nc/servers/${serverId}/pawn/buy/${l.id}`, {}, { params: { token } });
      toast.success(r.data.message);
      sfx?.success(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const instantPay = sell ? Math.floor(sell.value * rates.instant) * qty : 0;
  const askLo = sell ? Math.floor(sell.value * 0.3) : 0;
  const askHi = sell ? Math.floor(sell.value * 2) : 0;

  const TABS = [["browse", "Browse"], ["sell", "Sell"], ["mine", "My Counter"]];

  return (
    <div data-testid="pawn-app" className="p-3 flex flex-col gap-2 min-h-full">
      <div className="flex items-center gap-2 px-1 pb-1">
        <Store className="w-4 h-4 text-amber-300" />
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">Goldman &amp; Sons</p>
        <p className="ml-auto font-mono text-[7px] text-white/40 uppercase tracking-[0.1em]">est. 1987</p>
      </div>
      <div className="flex gap-1 px-1">
        {TABS.map(([id, lbl]) => (
          <button key={id} data-testid={`pawn-tab-${id}`} onClick={() => { sfx?.keyTick(); setTab(id); setSell(null); }}
            className={`flex-1 rounded-full py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] transition-colors ${tab === id ? "text-black font-bold" : "text-white/50 bg-white/5 hover:bg-white/10"}`}
            style={tab === id ? { backgroundColor: "#f59e0b" } : {}}>{lbl}</button>
        ))}
      </div>

      {tab === "browse" && (
        <div className="flex flex-col gap-2">
          {stock.length === 0 && <p data-testid="pawn-empty" className="font-mono text-[10px] text-white/40 text-center mt-10">Shelves are empty — pawn something and it lands here</p>}
          {stock.map((l) => (
            <div key={l.id} data-testid={`pawn-stock-${l.id.slice(0, 6)}`} className="rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><ItemIcon cat={l.cat} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm text-white">{l.qty > 1 ? `${l.qty}x ` : ""}{l.label}</p>
                <p className="font-mono text-[8px] text-white/40 truncate">{l.shop_stock ? "house stock" : `consigned by ${l.seller_name}`}</p>
              </div>
              {l.mine ? (
                <span className="font-mono text-[8px] uppercase tracking-[0.1em] text-amber-300 border border-amber-400/40 rounded-full px-2 py-1">yours</span>
              ) : (
                <button data-testid={`pawn-buy-${l.id.slice(0, 6)}`} onClick={() => buy(l)} disabled={busy}
                  className="font-mono text-[9px] font-bold uppercase tracking-[0.1em] px-3 py-2 rounded-full text-black transition-transform active:scale-95 disabled:opacity-40"
                  style={{ backgroundColor: "#f59e0b" }}>${l.total.toLocaleString()}</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "sell" && !sell && (
        <div className="flex flex-col gap-2">
          <p className="font-mono text-[8px] text-white/40 leading-relaxed px-1">Instant pawn pays {Math.round(rates.instant * 100)}% cash now. Consign at your own ask ({Math.round(rates.house_cut * 100)}% house cut) and it sells while you're offline.</p>
          {mine.valuables.length === 0 && <p data-testid="pawn-no-valuables" className="font-mono text-[10px] text-white/40 text-center mt-8">No valuables in your pockets — the city provides, one way or another</p>}
          {mine.valuables.map((v) => (
            <div key={v.key} data-testid={`pawn-val-${v.key}`} className="rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><ItemIcon cat={v.cat} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm text-white">{v.qty}x {v.label}</p>
                <p className="font-mono text-[8px] text-white/40">appraised ${v.value.toLocaleString()} each</p>
              </div>
              <button data-testid={`pawn-sell-${v.key}`} onClick={() => startSell(v)}
                className="font-mono text-[9px] font-bold uppercase tracking-[0.1em] px-3 py-2 rounded-full border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 transition-colors">Sell</button>
            </div>
          ))}
        </div>
      )}

      {tab === "sell" && sell && (
        <div data-testid="pawn-sell-sheet" className="rounded-2xl bg-white/5 border border-amber-400/20 p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <button data-testid="pawn-sell-back" onClick={() => setSell(null)} className="text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
            <ItemIcon cat={sell.cat} />
            <p className="font-sans font-semibold text-sm text-white">{sell.label} <span className="text-white/40 font-mono text-[9px]">· you have {sell.qty}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/50">Qty</p>
            <div className="flex items-center gap-1 ml-auto">
              <button data-testid="pawn-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-7 h-7 rounded-lg bg-white/10 text-white font-mono">−</button>
              <span data-testid="pawn-qty" className="w-8 text-center font-mono text-sm text-white">{qty}</span>
              <button data-testid="pawn-qty-plus" onClick={() => setQty((q) => Math.min(sell.qty, q + 1))} className="w-7 h-7 rounded-lg bg-white/10 text-white font-mono">+</button>
            </div>
          </div>
          <div className="flex gap-1">
            {[["instant", `Instant · $${instantPay.toLocaleString()}`], ["consign", "Consign"]].map(([m, lbl]) => (
              <button key={m} data-testid={`pawn-mode-${m}`} onClick={() => setMode(m)}
                className={`flex-1 rounded-full py-2 font-mono text-[9px] uppercase tracking-[0.1em] transition-colors ${mode === m ? "text-black font-bold" : "text-white/50 bg-white/5"}`}
                style={mode === m ? { backgroundColor: "#f59e0b" } : {}}>{lbl}</button>
            ))}
          </div>
          {mode === "consign" && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-white/50">Ask each</p>
                <input data-testid="pawn-ask" type="number" value={ask} min={askLo} max={askHi}
                  onChange={(e) => setAsk(Math.max(0, Math.min(askHi, parseInt(e.target.value || "0", 10))))}
                  className="ml-auto w-24 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 font-mono text-[11px] text-white text-right focus:outline-none focus:border-amber-400/50" />
              </div>
              <p className="font-mono text-[8px] text-white/40">Band ${askLo.toLocaleString()}–${askHi.toLocaleString()} · after the {Math.round(rates.house_cut * 100)}% cut you pocket <span className="text-amber-300">${Math.floor(ask * qty * (1 - rates.house_cut)).toLocaleString()}</span> · {rates.consign_hours}h window</p>
            </div>
          )}
          <button data-testid="pawn-confirm-sell" onClick={confirmSell} disabled={busy}
            className="mt-1 rounded-full py-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black transition-transform active:scale-95 disabled:opacity-40 flex items-center justify-center gap-2"
            style={{ backgroundColor: "#f59e0b" }}>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {mode === "instant" ? `Pawn for $${instantPay.toLocaleString()}` : "List on the shelf"}
          </button>
        </div>
      )}

      {tab === "mine" && (
        <div className="flex flex-col gap-2">
          {mine.listings.length === 0 && <p data-testid="pawn-no-listings" className="font-mono text-[10px] text-white/40 text-center mt-10">Nothing across the counter yet</p>}
          {mine.listings.map((l) => (
            <div key={l.id} data-testid={`pawn-my-${l.id.slice(0, 6)}`} className="rounded-2xl bg-white/5 border border-white/10 p-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0"><ItemIcon cat={l.cat} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-sans font-semibold text-sm text-white">{l.qty > 1 ? `${l.qty}x ` : ""}{l.label}</p>
                <p className="font-mono text-[8px] text-white/40">
                  {l.status === "listed" && `on the shelf · $${l.total.toLocaleString()}`}
                  {l.status === "sold" && `sold to ${l.buyer_name} · paid $${(l.payout || 0).toLocaleString()}`}
                  {l.status === "expired" && "expired — goods returned"}
                </p>
              </div>
              <span className={`font-mono text-[8px] uppercase tracking-[0.1em] rounded-full px-2 py-1 border ${
                l.status === "listed" ? "text-amber-300 border-amber-400/40" :
                l.status === "sold" ? "text-emerald-300 border-emerald-400/40" : "text-white/40 border-white/15"}`}>
                {l.status}
              </span>
            </div>
          ))}
          <p className="font-mono text-[7px] text-white/30 text-center uppercase tracking-[0.2em] mt-1 flex items-center justify-center gap-1"><HandCoins className="w-3 h-3" /> payouts land in your bank, letter follows</p>
        </div>
      )}
    </div>
  );
}
